import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { galleriesService } from '../services/galleries.service';
import { Gallery } from '../types';
import { GalleriesTable, GalleriesToolbar } from '../components/galleries/GalleriesTable';
import { GalleriesForm } from '../components/galleries/GalleriesForm';

export default function GalleriesPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState<Gallery | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: galleriesData, isLoading } = useQuery({
    queryKey: ['galleries', { page, limit, search }],
    queryFn: () => galleriesService.getList({ page, limit, search }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Gallery, 'id' | 'createdAt' | 'updatedAt' | 'images'>) => galleriesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setIsFormOpen(false);
      setEditingGallery(null);
      setNotification({ message: 'Галерея создана', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Gallery> }) => galleriesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setIsFormOpen(false);
      setEditingGallery(null);
      setNotification({ message: 'Галерея обновлена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => galleriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setNotification({ message: 'Галерея удалена', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const togglePublicMutation = useMutation({
    mutationFn: (id: number) => galleriesService.togglePublic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setNotification({ message: 'Статус изменен', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка', type: 'error' }),
  });

  const filteredGalleries = galleriesData?.data || [];
  const total = galleriesData?.pagination?.total || 0;

  const handleCreate = () => { setEditingGallery(null); setIsFormOpen(true); };
  const handleEdit = (gallery: Gallery) => { setEditingGallery(gallery); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    if (editingGallery) await updateMutation.mutateAsync({ id: editingGallery.id, data });
    else await createMutation.mutateAsync(data);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить галерею?')) deleteMutation.mutate(id); };
  const handleTogglePublic = (id: number) => togglePublicMutation.mutate(id);
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Галереи</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить галерею</Button>
      </Box>
      <GalleriesToolbar search={search} onSearchChange={(value) => { setSearch(value); setPage(1); }} />
      <GalleriesTable galleries={filteredGalleries} loading={isLoading} total={total} page={page} limit={limit} onPageChange={setPage} onEdit={handleEdit} onDelete={handleDelete} onTogglePublic={handleTogglePublic} />
      <GalleriesForm open={isFormOpen} onClose={() => setIsFormOpen(false)} onSubmit={handleSubmit} initialData={editingGallery as any} loading={createMutation.isPending || updateMutation.isPending} error={createMutation.error?.message || updateMutation.error?.message} />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
